import { useState } from 'react';
import {
	DataClassification,
	DataClassificationSchema,
	update,
} from '@/api/doc/data-classification';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateDataClassification({
	dataClassification,
}: {
	dataClassification: DataClassification;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedDataClassification: DataClassification) =>
			update(updatedDataClassification),
		onSuccess: () => {
			toast.success(
				`Success! The data classification has been successfully updated.`,
				{
					dismissible: true,
				},
			);
			setOpen(false);
			queryClient.invalidateQueries({ queryKey: ['data-classifications'] });
		},
		onError: () => {
			toast.error(`Error! The data classification could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<DataClassification>({
		resolver: zodResolver(DataClassificationSchema),
		defaultValues: dataClassification,
		mode: 'all',
	});

	const deletedDescription: string = !dataClassification.deletedOn
		? 'Toggle on to delete this data classification.'
		: 'Toggle off to not delete this data classification.';

	const deletedLabel: string = !dataClassification.deletedOn
		? 'Delete?'
		: 'Deleted';

	const onSubmit: SubmitHandler<DataClassification> = (
		values: DataClassification,
	) => mutate(values);

	form.watch();
	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Data Classification</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent className="overflow-scroll">
					<SheetHeader>
						<SheetTitle>Update Data Classification</SheetTitle>
					</SheetHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Data Classification Name</FormLabel>
										<FormControl>
											<Input {...field} placeholder="Data classification name" />
										</FormControl>
										<FormDescription>Name the data classification</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="deletedOn"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
										<div className="space-y-0.5">
											<FormLabel>{deletedLabel}</FormLabel>
											<FormDescription>{deletedDescription}</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={!!field.value}
												onCheckedChange={() =>
													form.setValue('deletedOn', field.value ? null : new Date())
												}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
